/**
 * i18n setup (CLAUDE.md 14.4, ADR-0003).
 *
 * Catalogues live in `shared/i18n/`, imported by *both* the frontend and the backend
 * report renderer. One directory means the UI and the PDF cannot drift apart -- a
 * check called one thing on screen and another in the report is the kind of defect
 * nobody notices until a client does.
 *
 * Never translated: symbols (f_ck, V_Rd,c, A_s), formulas, clause references, code
 * names, module short codes, unit symbols. Those are defined by the standard, not by
 * the language, and a French EC2 report says V_Rd,c because EN 1992 says V_Rd,c.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import enAdmin from '../../../shared/i18n/en/admin.json'
import enAuth from '../../../shared/i18n/en/auth.json'
import enCommon from '../../../shared/i18n/en/common.json'
import enErrors from '../../../shared/i18n/en/errors.json'
import enReport from '../../../shared/i18n/en/report.json'
import enSheets from '../../../shared/i18n/en/sheets.json'
import frAdmin from '../../../shared/i18n/fr/admin.json'
import frAuth from '../../../shared/i18n/fr/auth.json'
import frCommon from '../../../shared/i18n/fr/common.json'
import frErrors from '../../../shared/i18n/fr/errors.json'
import frReport from '../../../shared/i18n/fr/report.json'
import frSheets from '../../../shared/i18n/fr/sheets.json'

export const SUPPORTED_LOCALES = ['en', 'fr'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
}

const LOCALE_STORAGE_KEY = 'calcdesk.locale'

function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale
    }
  } catch {
    // Storage unavailable; fall through to the browser preference.
  }
  const browser = navigator.language.slice(0, 2)
  return (SUPPORTED_LOCALES as readonly string[]).includes(browser) ? (browser as Locale) : 'en'
}

void i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      auth: enAuth,
      report: enReport,
      errors: enErrors,
      admin: enAdmin,
      sheets: enSheets,
    },
    fr: {
      common: frCommon,
      auth: frAuth,
      report: frReport,
      errors: frErrors,
      admin: frAdmin,
      sheets: frSheets,
    },
  },
  lng: detectLocale(),
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: ['common', 'auth', 'report', 'errors', 'admin', 'sheets'],
  interpolation: {
    // React already escapes; double-escaping mangles apostrophes in French strings.
    escapeValue: false,
  },
  returnEmptyString: false,
})

export function setLocale(locale: Locale): void {
  void i18n.changeLanguage(locale)
  document.documentElement.lang = locale
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Non-fatal: the choice simply will not survive a reload.
  }
}

document.documentElement.lang = i18n.language

export default i18n
